import { useState } from 'react'
import {
  Store, Phone, Building2, Truck, Save, RotateCcw, ExternalLink,
  Mail, MapPin, Clock, Facebook, Youtube, Instagram, MessageCircle,
  CreditCard, QrCode, Banknote, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatVND } from '@/lib/formatters'
import { useAdminSettingsStore, DEFAULT_SETTINGS, type ShopSettings } from '../../../state/settingsStore'
import { VN_BANKS } from '../../../config/bank-list'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'

type Tab = 'shop' | 'contact' | 'bank' | 'shipping'

const TABS: { key: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'shop',     label: 'Cửa hàng',   icon: Store },
  { key: 'contact',  label: 'Liên hệ',    icon: Phone },
  { key: 'bank',     label: 'Ngân hàng',  icon: Building2 },
  { key: 'shipping', label: 'Vận chuyển', icon: Truck },
]

export function SettingsPage() {
  const saved = useAdminSettingsStore((s) => s.settings)
  const updateStore = useAdminSettingsStore((s) => s.update)
  const resetStore = useAdminSettingsStore((s) => s.reset)
  const push = useToast((s) => s.push)

  const [tab, setTab] = useState<Tab>('shop')
  const [v, setV] = useState<ShopSettings>(saved)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [resetConfirm, setResetConfirm] = useState(false)

  const dirty = JSON.stringify(v) !== JSON.stringify(saved)

  const upd = <K extends keyof ShopSettings>(k: K, val: ShopSettings[K]) => {
    setV((s) => ({ ...s, [k]: val }))
    setErrors((e) => ({ ...e, [k as string]: '' }))
  }

  const handleSave = () => {
    const errs: Record<string, string> = {}
    if (!v.shopName.trim())       errs.shopName = 'Vui lòng nhập tên cửa hàng'
    if (!v.hotline.trim())        errs.hotline = 'Vui lòng nhập hotline'
    if (!v.email.trim())          errs.email = 'Vui lòng nhập email'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) errs.email = 'Email không hợp lệ'
    if (v.qrEnabled) {
      if (!v.bankAccountNo.trim())   errs.bankAccountNo = 'Cần số tài khoản khi bật QR'
      if (!v.bankAccountName.trim()) errs.bankAccountName = 'Cần tên chủ tài khoản'
    }
    if (v.freeShippingThreshold < 0) errs.freeShippingThreshold = 'Phải >= 0'
    if (v.defaultShippingFee < 0)    errs.defaultShippingFee = 'Phải >= 0'
    if (Object.keys(errs).length) {
      setErrors(errs)
      push('error', 'Vui lòng kiểm tra lại các trường bị lỗi')
      // Nhảy sang tab có lỗi đầu tiên
      const first = Object.keys(errs)[0]
      if (['shopName', 'slogan', 'description', 'address', 'workingHours', 'logoUrl', 'faviconUrl'].includes(first)) setTab('shop')
      else if (['hotline', 'email', 'facebookUrl', 'youtubeUrl', 'instagramUrl', 'zaloPhone'].includes(first)) setTab('contact')
      else if (first.startsWith('bank') || ['qrEnabled', 'codEnabled'].includes(first)) setTab('bank')
      else setTab('shipping')
      return
    }
    updateStore(v)
    push('success', 'Đã lưu cài đặt')
  }

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const ErrorMsg = ({ msg }: { msg?: string }) => msg ? <div className="text-xs text-danger mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {msg}</div> : null

  const qrPreview = v.qrEnabled && v.bankAccountNo
    ? `https://img.vietqr.io/image/${v.bankBin}-${v.bankAccountNo}-compact2.png?amount=1000000&addInfo=XeDien%20DEMO&accountName=${encodeURIComponent(v.bankAccountName)}`
    : ''

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Cài đặt cửa hàng</h1>
          <p className="text-sm text-muted mt-1">Thông tin hiển thị trên toàn site + cấu hình thanh toán & vận chuyển</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setResetConfirm(true)}
            className="h-11 px-4 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Khôi phục mặc định
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty}
            className="h-11 px-6 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold text-sm inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Lưu thay đổi
          </button>
        </div>
      </div>

      {dirty && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Có thay đổi chưa lưu. Nhấn "Lưu thay đổi" để áp dụng.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
        {/* Side tabs */}
        <nav className="bg-surface border border-border rounded-xl shadow-card p-2 h-fit">
          <ul className="space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon
              const active = tab === t.key
              return (
                <li key={t.key}>
                  <button
                    onClick={() => setTab(t.key)}
                    className={[
                      'w-full h-11 px-3 rounded-md text-sm font-medium inline-flex items-center gap-3 transition',
                      active ? 'bg-brand-50 text-brand-700' : 'text-text hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <Icon className="w-5 h-5" />
                    {t.label}
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Content */}
        <div className="space-y-5">
          {tab === 'shop' && (
            <>
              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Thông tin cửa hàng</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Tên cửa hàng <span className="text-danger">*</span></div>
                    <input value={v.shopName} onChange={(e) => upd('shopName', e.target.value)} className={inputCls(errors.shopName)} />
                    <ErrorMsg msg={errors.shopName} />
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Slogan</div>
                    <input value={v.slogan} onChange={(e) => upd('slogan', e.target.value)} className={inputCls()} />
                  </label>
                </div>
                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">Mô tả cửa hàng</div>
                  <textarea
                    value={v.description}
                    onChange={(e) => upd('description', e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
                  />
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Địa chỉ trụ sở</div>
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                      <input value={v.address} onChange={(e) => upd('address', e.target.value)} className={inputCls() + ' !pl-10'} />
                    </div>
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Giờ làm việc</div>
                    <div className="relative">
                      <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                      <input value={v.workingHours} onChange={(e) => upd('workingHours', e.target.value)} className={inputCls() + ' !pl-10'} />
                    </div>
                  </label>
                </div>
              </section>

              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Nhận diện thương hiệu</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Logo URL</div>
                    <input value={v.logoUrl} onChange={(e) => upd('logoUrl', e.target.value)} className={inputCls()} placeholder="https://..." />
                    {v.logoUrl && (
                      <div className="mt-2 w-24 h-24 rounded-lg bg-slate-100 border border-border overflow-hidden">
                        <img src={v.logoUrl} alt="logo" className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                      </div>
                    )}
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Favicon URL</div>
                    <input value={v.faviconUrl} onChange={(e) => upd('faviconUrl', e.target.value)} className={inputCls()} placeholder="https://..." />
                    {v.faviconUrl && (
                      <div className="mt-2 w-10 h-10 rounded bg-slate-100 border border-border overflow-hidden">
                        <img src={v.faviconUrl} alt="favicon" className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                      </div>
                    )}
                  </label>
                </div>
              </section>
            </>
          )}

          {tab === 'contact' && (
            <>
              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Liên hệ chính</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Hotline <span className="text-danger">*</span></div>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                      <input value={v.hotline} onChange={(e) => upd('hotline', e.target.value)} className={inputCls(errors.hotline) + ' !pl-10'} />
                    </div>
                    <ErrorMsg msg={errors.hotline} />
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Email <span className="text-danger">*</span></div>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                      <input type="email" value={v.email} onChange={(e) => upd('email', e.target.value)} className={inputCls(errors.email) + ' !pl-10'} />
                    </div>
                    <ErrorMsg msg={errors.email} />
                  </label>
                </div>
                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">SĐT Zalo</div>
                  <div className="relative">
                    <MessageCircle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                    <input value={v.zaloPhone} onChange={(e) => upd('zaloPhone', e.target.value)} className={inputCls() + ' !pl-10'} placeholder="0912345678" />
                  </div>
                </label>
              </section>

              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Mạng xã hội</h3>
                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">Facebook URL</div>
                  <div className="relative">
                    <Facebook className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1877F2]" />
                    <input value={v.facebookUrl} onChange={(e) => upd('facebookUrl', e.target.value)} className={inputCls() + ' !pl-10'} placeholder="https://facebook.com/..." />
                  </div>
                </label>
                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">YouTube URL</div>
                  <div className="relative">
                    <Youtube className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-red-600" />
                    <input value={v.youtubeUrl} onChange={(e) => upd('youtubeUrl', e.target.value)} className={inputCls() + ' !pl-10'} placeholder="https://youtube.com/..." />
                  </div>
                </label>
                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">Instagram URL</div>
                  <div className="relative">
                    <Instagram className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-600" />
                    <input value={v.instagramUrl} onChange={(e) => upd('instagramUrl', e.target.value)} className={inputCls() + ' !pl-10'} placeholder="https://instagram.com/..." />
                  </div>
                </label>
              </section>
            </>
          )}

          {tab === 'bank' && (
            <>
              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Phương thức thanh toán</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className={[
                    'rounded-xl border-2 p-4 cursor-pointer transition flex items-start gap-3',
                    v.codEnabled ? 'border-brand-600 bg-brand-50' : 'border-border bg-surface hover:border-brand-500',
                  ].join(' ')}>
                    <input type="checkbox" checked={v.codEnabled} onChange={(e) => upd('codEnabled', e.target.checked)} className="w-5 h-5 accent-brand-600 rounded mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-semibold text-text">
                        <Banknote className="w-5 h-5 text-brand-600" /> COD (Thanh toán khi nhận hàng)
                      </div>
                      <div className="text-xs text-muted mt-1">Khách trả tiền mặt cho shipper lúc nhận hàng</div>
                    </div>
                  </label>
                  <label className={[
                    'rounded-xl border-2 p-4 cursor-pointer transition flex items-start gap-3',
                    v.qrEnabled ? 'border-brand-600 bg-brand-50' : 'border-border bg-surface hover:border-brand-500',
                  ].join(' ')}>
                    <input type="checkbox" checked={v.qrEnabled} onChange={(e) => upd('qrEnabled', e.target.checked)} className="w-5 h-5 accent-brand-600 rounded mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-semibold text-text">
                        <QrCode className="w-5 h-5 text-brand-600" /> QR VietQR
                      </div>
                      <div className="text-xs text-muted mt-1">Khách quét QR chuyển khoản trực tiếp</div>
                    </div>
                  </label>
                </div>
              </section>

              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text inline-flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-brand-600" /> Tài khoản nhận tiền
                </h3>

                <label className="block">
                  <div className="text-sm font-medium text-text mb-1.5">Ngân hàng</div>
                  <select
                    value={v.bankBin}
                    onChange={(e) => {
                      const bank = VN_BANKS.find((b) => b.bin === e.target.value)
                      if (bank) {
                        upd('bankBin', bank.bin)
                        upd('bankShortName', bank.short)
                      }
                    }}
                    className={inputCls()}
                  >
                    {VN_BANKS.map((b) => (
                      <option key={b.bin} value={b.bin}>{b.short} — {b.full}</option>
                    ))}
                  </select>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Số tài khoản {v.qrEnabled && <span className="text-danger">*</span>}</div>
                    <input value={v.bankAccountNo} onChange={(e) => upd('bankAccountNo', e.target.value.replace(/\s/g, ''))} className={inputCls(errors.bankAccountNo)} placeholder="0123456789" />
                    <ErrorMsg msg={errors.bankAccountNo} />
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Chủ tài khoản {v.qrEnabled && <span className="text-danger">*</span>}</div>
                    <input value={v.bankAccountName} onChange={(e) => upd('bankAccountName', e.target.value.toUpperCase())} className={inputCls(errors.bankAccountName)} placeholder="NGUYEN VAN A" />
                    <ErrorMsg msg={errors.bankAccountName} />
                  </label>
                </div>

                {qrPreview && (
                  <div className="rounded-lg border border-dashed border-border bg-slate-50 p-5">
                    <div className="text-sm font-medium text-text mb-3">Xem trước QR (ví dụ 1.000.000đ)</div>
                    <div className="flex items-start gap-5">
                      <div className="w-[180px] aspect-square bg-white border border-border rounded-lg overflow-hidden p-2 shrink-0">
                        <img
                          src={qrPreview}
                          alt="QR Preview"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23f1f5f9"/></svg>'
                          }}
                        />
                      </div>
                      <div className="flex-1 text-sm space-y-1.5">
                        <div><span className="text-muted">Ngân hàng:</span> <span className="font-semibold text-text">{v.bankShortName}</span></div>
                        <div><span className="text-muted">Số TK:</span> <span className="font-mono font-semibold text-text">{v.bankAccountNo}</span></div>
                        <div><span className="text-muted">Chủ TK:</span> <span className="font-semibold text-text">{v.bankAccountName}</span></div>
                        <div className="text-xs text-muted pt-2 border-t border-border mt-2">
                          QR được sinh realtime từ <span className="font-mono">img.vietqr.io</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}

          {tab === 'shipping' && (
            <>
              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Vận chuyển</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Miễn phí vận chuyển từ (đ)</div>
                    <input type="number" value={v.freeShippingThreshold} onChange={(e) => upd('freeShippingThreshold', Number(e.target.value))} className={inputCls(errors.freeShippingThreshold)} />
                    <div className="text-xs text-muted mt-1">Hiện tại: {formatVND(v.freeShippingThreshold)} — đơn trên ngưỡng này miễn phí ship</div>
                    <ErrorMsg msg={errors.freeShippingThreshold} />
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Phí vận chuyển mặc định (đ)</div>
                    <input type="number" value={v.defaultShippingFee} onChange={(e) => upd('defaultShippingFee', Number(e.target.value))} className={inputCls(errors.defaultShippingFee)} />
                    <div className="text-xs text-muted mt-1">Hiện tại: {formatVND(v.defaultShippingFee)}</div>
                    <ErrorMsg msg={errors.defaultShippingFee} />
                  </label>
                </div>
              </section>

              <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
                <h3 className="font-semibold text-text">Chính sách</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Thời gian đổi trả (ngày)</div>
                    <input type="number" value={v.returnPolicyDays} onChange={(e) => upd('returnPolicyDays', Number(e.target.value))} className={inputCls()} />
                    <div className="text-xs text-muted mt-1">Khách có {v.returnPolicyDays} ngày để đổi trả khi phát hiện lỗi</div>
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium text-text mb-1.5">Bảo hành (tháng)</div>
                    <input type="number" value={v.warrantyMonths} onChange={(e) => upd('warrantyMonths', Number(e.target.value))} className={inputCls()} />
                    <div className="text-xs text-muted mt-1">Bảo hành chính hãng {v.warrantyMonths} tháng (~{Math.round(v.warrantyMonths / 12)} năm)</div>
                  </label>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 rounded-xl p-5 flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-lg bg-brand-600 text-white flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-text">Mọi thay đổi được lưu vào trình duyệt</div>
          <div className="text-sm text-muted mt-0.5">Khi có backend sẽ chuyển qua API thực. Hiện tại dùng localStorage để preview.</div>
        </div>
        <Link
          to="/lien-he"
          target="_blank"
          className="h-10 px-4 rounded-md bg-surface border border-border text-text hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" /> Xem trang liên hệ khách
        </Link>
      </div>

      <ConfirmDialog
        open={resetConfirm}
        title="Khôi phục mặc định"
        message="Toàn bộ cài đặt sẽ reset về giá trị gốc. Tiếp tục?"
        confirmLabel="Khôi phục"
        tone="brand"
        onConfirm={() => { resetStore(); setV(DEFAULT_SETTINGS); push('info', 'Đã khôi phục cài đặt mặc định'); setResetConfirm(false) }}
        onCancel={() => setResetConfirm(false)}
      />
    </div>
  )
}
